/**
 * Phase 4 — Public Trust Center.
 * Lives at /trust.
 *
 * Polish pass: methodology-grade hero, framework crests, pillar cards,
 * "Download SOC 2 readiness summary" PDF, "Talk to Sales" CTA hook,
 * and the per-policy public viewer at /trust/policies/<slug>.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  ShieldCheck, FileText, ArrowRight, ArrowLeft, Calendar,
  Download, MessageSquare, Sparkles, Lock, GitBranch, FileCheck2,
  Activity,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DecryptedHeading from "../components/docs/DecryptedHeading";
import EmailPostureCard from "../components/EmailPostureCard";
import TrustFreshnessCard from "../components/TrustFreshnessCard";
import TrustLivePostureTile from "../components/TrustLivePostureTile";
import TrustPostureHistory from "../components/TrustPostureHistory";
import TrustAuditUpdatesForm from "../components/TrustAuditUpdatesForm";
import { formatLongDate } from "../lib/dateFormat";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: API, withCredentials: true });

const STATUS_PALETTE = {
  met:     "#1FDD83",
  partial: "#FFB000",
  gap:     "#FF6E6E",
};

const PILLARS = [
  {
    id: "live",
    icon: Sparkles,
    title: "Live, not aspirational",
    desc: "Every percentage on this page is generated from our internal GRC console. We don't \"prepare\" the numbers for visitors — what you see is what we see.",
    accent: "var(--qt-cyan)",
  },
  {
    id: "stamped",
    icon: GitBranch,
    title: "Versioned policies",
    desc: "Every published policy carries an immutable version number. Diff any two versions to see exactly what changed since the last attestation.",
    accent: "#1FDD83",
  },
  {
    id: "frameworks",
    icon: FileCheck2,
    title: "Major frameworks mapped",
    desc: "Controls are mapped to SOC 2, ISO/IEC 27001:2022, NIST CSF 2.0, NIST SP 800-53 Rev. 5, NIST SP 800-171 Rev. 3, EU DORA, and FFIEC — the authorities institutional and federal procurement actually ask for.",
    accent: "var(--qt-cyan)",
  },
  {
    id: "evidence",
    icon: Lock,
    title: "Evidence on request",
    desc: "Auditor-grade evidence (SOC 2 Type II reports, ISO certificates, signed attestations) is available under NDA. Email trust@qortrace.com to request the bundle.",
    accent: "var(--qt-amber)",
  },
];


export default function Trust() {
  useDocumentTitle("Trust Center — QorTrace");
  usePageMeta({
    title: "Trust Center — QorTrace",
    description: "Live 9-layer security posture for QorTrace itself — TLS, HSTS, CSP, DNSSEC, MTA-STS, audit-log integrity, evidence freshness, vulnerability scans, PQC readiness. Refreshed every minute.",
    canonicalPath: "/trust",
  });
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    client.get("/api/trust/posture")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load."));
  }, []);

  // Aggregate met-percentage averaged across the 4 frameworks for the
  // hero's headline number. Weighted by in-scope controls so a tiny
  // framework can't pull it disproportionately.
  const overallMetPct = (() => {
    if (!data?.frameworks?.length) return null;
    let met = 0, denom = 0;
    for (const f of data.frameworks) {
      met   += f.met;
      denom += (f.controls_total - f.not_applicable);
    }
    return denom > 0 ? Math.round(100 * met / denom) : 0;
  })();

  const totalControls = data?.frameworks?.reduce((a, f) => a + f.controls_total, 0) || 0;

  return (
    <div className="min-h-screen flex flex-col qt-radial-bg" data-testid="public-trust-page">
      <Header />
      <main className="flex-1 pt-12 pb-20">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8">

          {/* ----- Hero band ----- */}
          <div data-testid="trust-hero" className="pt-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="qt-mono text-[10px] px-2.5 py-1 rounded inline-flex items-center gap-1.5"
                style={{
                  color: "#1FDD83",
                  border: "1px solid rgba(123,217,168,0.4)",
                  background: "rgba(123,217,168,0.08)",
                  letterSpacing: "0.16em",
                  fontWeight: 700,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#1FDD83", display: "inline-block" }} />
                LIVE · GENERATED FROM SOURCE
              </span>
              <span
                className="qt-mono text-[11px]"
                style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}
              >
                TRUST CENTER · UPDATED CONTINUOUSLY
              </span>
              <Link to="/status"
                    data-testid="trust-status-link"
                    className="qt-mono text-[10px] px-2.5 py-1 rounded inline-flex items-center gap-1.5"
                    style={{ color: "var(--qt-cyan)",
                             border: "1px solid rgba(0,229,255,0.35)",
                             background: "rgba(0,229,255,0.05)",
                             letterSpacing: "0.16em", fontWeight: 700,
                             textDecoration: "none" }}>
                <Activity size={10} /> SERVICE STATUS →
              </Link>
            </div>

            <DecryptedHeading
              target="Compliance posture, in the open."
              className="qt-h2 qt-docs-decrypt text-4xl sm:text-5xl mt-4"
              testId="trust-hero-title"
            />

            <p
              className="mt-4 max-w-3xl text-base"
              style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}
            >
              QorTrace maps every operational and platform control we run against
              <strong style={{ color: "#fff" }}> SOC 2 Type II</strong>,{" "}
              <strong style={{ color: "#fff" }}>ISO/IEC 27001:2022</strong>,{" "}
              <strong style={{ color: "#fff" }}>NIST CSF 2.0</strong>,{" "}
              the EU's <strong style={{ color: "#fff" }}>DORA</strong>, and the{" "}
              <strong style={{ color: "#fff" }}>FFIEC</strong> IT-Examination handbook.
              The numbers below come straight from our internal GRC console — no marketing layer in the middle.
            </p>

            {data && (
              <div className="mt-6 flex items-center gap-x-6 gap-y-3 flex-wrap" data-testid="trust-hero-stats">
                <div className="flex items-center gap-2">
                  <span className="qt-mono text-2xl font-bold" style={{ color: "var(--qt-cyan)", letterSpacing: "-0.02em" }}>
                    {overallMetPct ?? "—"}<span style={{ fontSize: 12, color: "var(--qt-text-3)" }}>%</span>
                  </span>
                  <span className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em" }}>
                    OVERALL MET
                  </span>
                </div>
                <span className="qt-mono text-base" style={{ color: "var(--qt-border)" }}>·</span>
                <div className="flex items-center gap-2">
                  <span className="qt-mono text-2xl font-bold" style={{ color: "var(--qt-cyan)", letterSpacing: "-0.02em" }}>
                    {totalControls}
                  </span>
                  <span className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em" }}>
                    CONTROLS MAPPED
                  </span>
                </div>
                <span className="qt-mono text-base" style={{ color: "var(--qt-border)" }}>·</span>
                <div className="flex items-center gap-2">
                  <span className="qt-mono text-2xl font-bold" style={{ color: "var(--qt-cyan)", letterSpacing: "-0.02em" }}>
                    {data.policies?.length || 0}
                  </span>
                  <span className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em" }}>
                    POLICIES PUBLISHED
                  </span>
                </div>
                <span className="qt-mono text-base" style={{ color: "var(--qt-border)" }}>·</span>
                <div className="flex items-center gap-2">
                  <span className="qt-mono text-2xl font-bold" style={{ color: "var(--qt-cyan)", letterSpacing: "-0.02em" }}>
                    {data.frameworks?.length || 0}
                  </span>
                  <span className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em" }}>
                    FRAMEWORKS
                  </span>
                </div>
              </div>
            )}

            <div className="mt-7 flex items-center gap-3 flex-wrap" data-testid="trust-hero-ctas">
              <a
                href={`${API}/api/trust/posture.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trust-download-pdf"
                className="qt-mono text-xs"
                style={{
                  padding: "10px 18px",
                  background: "var(--qt-cyan)",
                  color: "#0B1324",
                  border: "none",
                  borderRadius: 4,
                  textDecoration: "none",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Download size={12} /> Download readiness summary (PDF)
              </a>
              <a
                href="mailto:trust@qortrace.com?subject=SOC%202%20Type%20II%20report%20request"
                data-testid="trust-soc2-request"
                className="qt-mono text-xs"
                style={{
                  padding: "10px 18px",
                  border: "1px solid var(--qt-border)",
                  background: "transparent",
                  color: "var(--qt-text-2)",
                  borderRadius: 4,
                  textDecoration: "none",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <FileText size={12} /> Request SOC 2 Type II report
              </a>
              <Link
                to="/audit/talk-to-sales"
                data-testid="trust-talk-to-sales"
                className="qt-mono text-xs"
                style={{
                  padding: "10px 18px",
                  border: "1px solid rgba(0,229,255,0.35)",
                  background: "rgba(0,229,255,0.06)",
                  color: "var(--qt-cyan)",
                  borderRadius: 4,
                  textDecoration: "none",
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <MessageSquare size={12} /> Talk to Sales
              </Link>
            </div>
          </div>

          {/* ----- Pillars ----- */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12" data-testid="trust-pillars">
            {PILLARS.map(({ id, icon: Ic, title, desc, accent }) => (
              <div
                key={id}
                data-testid={`trust-pillar-${id}`}
                className="qt-card"
                style={{ padding: 18, background: "rgba(4,9,20,0.5)", borderColor: "var(--qt-border-soft)" }}
              >
                <Ic size={18} style={{ color: accent }} />
                <h3 className="qt-mono mt-3" style={{ color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: "-0.01em" }}>
                  {title}
                </h3>
                <p className="text-[13px] mt-2" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>

          {err && (
            <div className="mt-8 qt-card" style={{ borderColor: "rgba(255,110,110,0.4)" }}>
              <div className="qt-mono text-sm" style={{ color: "#FF8C8C" }}>{err}</div>
            </div>
          )}

          {/* ----- Posture grid ----- */}
          {data && (
            <section className="mt-16" data-testid="trust-posture-section">
              <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>POSTURE BY FRAMEWORK</div>
              <h2 className="text-2xl font-semibold mt-2" style={{ color: "#fff" }}>
                Live control posture
              </h2>
              {data.last_attested && (
                <p className="qt-mono text-xs mt-2" style={{ color: "var(--qt-text-3)" }}>
                  <Calendar size={11} className="inline mr-1" />
                  Last policy attested {formatLongDate(data.last_attested)}
                </p>
              )}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="trust-posture-grid">
                {data.frameworks.map((fw) => (
                  <FrameworkCard key={fw.id} fw={fw} />
                ))}
              </div>
            </section>
          )}

          {/* ----- Live security posture rollup (Phase 12.8) ----- */}
          <div className="mt-8">
            <TrustLivePostureTile />
          </div>

          {/* ----- Posture history sparklines (Phase 12.9) ----- */}
          <div className="mt-6">
            <TrustPostureHistory />
          </div>

          {/* ----- Email auth posture (Phase 12.3) ----- */}
          <EmailPostureCard />

          {/* ----- Daily self-audit freshness (Phase 12.4) ----- */}
          <TrustFreshnessCard />

          {/* ----- Audit-updates monthly digest (Phase 12.5) ----- */}
          <TrustAuditUpdatesForm />

          {/* ----- Policies ----- */}
          {data && (
            <section className="mt-16" data-testid="trust-policies-section">
              <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>PUBLISHED POLICIES</div>
              <h2 className="text-2xl font-semibold mt-2" style={{ color: "#fff" }}>
                {data.policies.length} polic{data.policies.length === 1 ? "y" : "ies"} attested
              </h2>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.policies.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/trust/policies/${p.slug}`}
                    data-testid={`trust-policy-${p.slug}`}
                    className="qt-card flex items-center gap-3"
                    style={{ padding: "14px 16px", textDecoration: "none" }}
                  >
                    <FileText size={14} style={{ color: "var(--qt-cyan)" }} />
                    <div className="flex-1 min-w-0">
                      <div style={{ color: "#fff", fontWeight: 500 }}>{p.title}</div>
                      <div className="qt-mono text-xs mt-1" style={{ color: "var(--qt-text-3)" }}>
                        v{p.current_version}
                        {p.published_at ? ` · ${formatLongDate(p.published_at)}` : ""}
                      </div>
                    </div>
                    <ArrowRight size={14} style={{ color: "var(--qt-text-2)" }} />
                  </Link>
                ))}
                {data.policies.length === 0 && (
                  <div className="qt-mono text-sm md:col-span-2" style={{ color: "var(--qt-text-3)" }}>
                    Policy library is being prepared. Check back soon.
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ----- Methodology footer ----- */}
          <section className="mt-16" data-testid="trust-disclaimers">
            <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>METHODOLOGY</div>
            <p className="qt-mono text-xs mt-3" style={{ color: "var(--qt-text-2)", lineHeight: 1.85, maxWidth: 820 }}>
              Each framework's <strong style={{ color: "#fff" }}>met %</strong> is the share
              of in-scope controls (excluding N/A) marked <em>met</em> by our security team.
              Controls in the <em>partial</em> state have a remediation owner and a target
              <strong style={{ color: "#fff" }}>SOC 2</strong> follows the AICPA Trust Service Criteria,
              <strong style={{ color: "#fff" }}> ISO/IEC 27001</strong> the 2022 Annex A taxonomy,{" "}
              <strong style={{ color: "#fff" }}>NIST CSF 2.0</strong> the 2024 Cybersecurity Framework (Govern · Identify · Protect · Detect · Respond · Recover),{" "}
              <strong style={{ color: "#fff" }}>NIST SP 800-53 Rev. 5</strong> the federal control catalog (17 families: AC, AT, AU, CA, CM, CP, IA, IR, MA, MP, PE, PL, PS, RA, SA, SC, SI),{" "}
              <strong style={{ color: "#fff" }}>NIST SP 800-171 Rev. 3</strong> the CUI protection baseline,{" "}
              <strong style={{ color: "#fff" }}>DORA</strong> the EU 2022/2554 chapter structure,{" "}
              and <strong style={{ color: "#fff" }}>FFIEC</strong> the IT-Examination handbook booklets.
              QorTrace's cryptographic engines themselves align with NIST PQC: <strong style={{ color: "#fff" }}>FIPS 203 (ML-KEM)</strong>, <strong style={{ color: "#fff" }}>FIPS 204 (ML-DSA)</strong>, and <strong style={{ color: "#fff" }}>FIPS 205 (SLH-DSA)</strong>.
              For a SOC 2 Type II report or a signed compliance receipt, contact{" "}
              <a href="mailto:trust@qortrace.com"
                 style={{ color: "var(--qt-cyan)", textDecoration: "underline" }}>trust@qortrace.com</a>.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}


function FrameworkCard({ fw }) {
  const denom = fw.controls_total - fw.not_applicable;
  const seg = (n) => denom > 0 ? `${(100 * n / denom).toFixed(1)}%` : "0%";
  return (
    <div className="qt-card" data-testid={`trust-fw-${fw.id}`} style={{ padding: 22 }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="qt-mono text-xs" style={{ color: "var(--qt-text-3)", letterSpacing: "0.18em" }}>
            {fw.authority}
          </div>
          <div className="text-lg font-semibold mt-1" style={{ color: "#fff" }}>{fw.label}</div>
        </div>
        <div className="text-right">
          <div className="qt-mono" style={{ fontSize: 36, color: "var(--qt-cyan)", lineHeight: 1, fontWeight: 700, letterSpacing: "-0.02em" }}>
            {fw.met_pct}<span style={{ fontSize: 14, color: "var(--qt-text-3)" }}>%</span>
          </div>
          <div className="qt-mono text-xs mt-1" style={{ color: "var(--qt-text-3)" }}>met</div>
        </div>
      </div>

      <div style={{ marginTop: 18, height: 8, borderRadius: 4, overflow: "hidden", display: "flex", background: "rgba(255,255,255,0.05)" }}>
        <div style={{ width: seg(fw.met),     background: STATUS_PALETTE.met }} title={`${fw.met} met`} />
        <div style={{ width: seg(fw.partial), background: STATUS_PALETTE.partial }} title={`${fw.partial} partial`} />
        <div style={{ width: seg(fw.gap),     background: STATUS_PALETTE.gap }} title={`${fw.gap} gap`} />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <Stat label="Met"     value={fw.met}     accent={STATUS_PALETTE.met} />
        <Stat label="Partial" value={fw.partial} accent={STATUS_PALETTE.partial} />
        <Stat label="Gap"     value={fw.gap}     accent={STATUS_PALETTE.gap} />
      </div>
      <div className="qt-mono text-xs mt-3 flex items-center gap-2" style={{ color: "var(--qt-text-3)" }}>
        <ShieldCheck size={11} />
        {denom} in-scope · {fw.not_applicable} N/A
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="qt-mono text-xs" style={{ color: "var(--qt-text-3)", letterSpacing: "0.14em" }}>{label.toUpperCase()}</div>
      <div className="qt-mono" style={{ fontSize: 18, color: accent, marginTop: 2 }}>{value}</div>
    </div>
  );
}


// ----------------------------------------------------------------------------
// Public single-policy view at /trust/policies/:slug.
// ----------------------------------------------------------------------------

export function TrustPolicyView() {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    client.get(`/api/trust/policies/${slug}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Policy not found."));
  }, [slug]);

  return (
    <div className="min-h-screen flex flex-col qt-radial-bg" data-testid="public-trust-policy">
      <Header />
      <main className="flex-1 pt-12 pb-20">
        <div className="max-w-[820px] mx-auto px-6 sm:px-8">
          <Link to="/trust" className="qt-mono text-xs" style={{ color: "var(--qt-text-2)" }}>
            <ArrowLeft size={12} className="inline mr-1" /> TRUST CENTER
          </Link>

          {err && (
            <div className="mt-8 qt-card" style={{ borderColor: "rgba(255,110,110,0.4)" }}>
              <div className="qt-mono text-sm" style={{ color: "#FF8C8C" }}>{err}</div>
            </div>
          )}

          {doc && (
            <article className="mt-4" data-testid="public-trust-policy-content">
              <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
                POLICY · v{doc.version}
                {doc.published_at ? ` · ${formatLongDate(doc.published_at)}` : ""}
              </div>
              <h1 className="qt-h2 text-3xl sm:text-4xl mt-3" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
                {doc.title}
              </h1>
              {doc.change_summary && (
                <p className="qt-mono text-xs mt-3" style={{ color: "var(--qt-text-3)" }}>
                  Change in this version: {doc.change_summary}
                </p>
              )}
              <div className="qt-prose mt-8" style={{ color: "var(--qt-text-2)" }}>
                <ReactMarkdown>{doc.body_md}</ReactMarkdown>
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
